import client from './client';
import {
  countries,
  facilityConfirmProblem,
  facilityDeleteProblem,
  facilityEditProblem,
  facilityOrder,
  facilityOrderAddItemPhoto,
  facilityOrderAddPackages,
  facilityOrderAddProblemPhoto,
  facilityOrderBag,
  facilityOrderBagAddItem,
  facilityOrderBagAddSpecialItem,
  facilityOrderBagConnectTag,
  facilityOrderBagEditItem,
  facilityOrderBagEditSpecialItem,
  facilityOrderBagFinishItemisation,
  facilityOrderBagRemoveItem,
  facilityOrderBagRemoveTag,
  facilityOrderBagRemoveTags,
  facilityOrderEditItemPhoto,
  facilityOrderEditPackages,
  facilityOrderEditProblemPhoto,
  facilityOrderFinishPacking,
  facilityOrderPackBags,
  facilityOrderRemoveProblemPhoto,
  facilityProblemReport,
  facilityProblemsList,
  facilityServiceMismatchList,
  facilityServices,
  facilityWorkerQRScan,
  getFacility,
  getFacilityOrderItem,
  getFacilityOrdersFuturePastSchema,
  getFacilityOrdersSchema,
  getFacilityOrdersTodaySchema,
  getFacilityOrdersTomorrowSchema,
  getFacilityPastOrdersSchema,
  getFacilityServiceFilterSchema,
  receivedOrdersWithProblems,
  signInFacilityWorkerSchema,
  signUpFacilityWorkerSchema,
  supportCenterOrderList,
  surveyAnswered,
} from './schemas';
import {
  AddFacilityOrderItemPhotoApi,
  EditFacilityOrderItemPhotoApi,
  FacilityOrder,
  FacilityOrderAddPackagesApi,
  FacilityOrderApi,
  FacilityOrderBag,
  FacilityOrderBagAddItemApi,
  FacilityOrderBagAddSpecialItemApi,
  FacilityOrderBagApi,
  FacilityOrderBagEditItemApi,
  FacilityOrderBagEditSpecialItemApi,
  FacilityOrderBagFinishItemisationApi,
  FacilityOrderBagItem,
  FacilityOrderBagItemPhotos,
  FacilityOrderBagItemProblemPhotos,
  FacilityOrderBagRemoveItemApi,
  FacilityOrderEditPackagesApi,
  FacilityOrderFinishPackingApi,
  FacilityOrderItemApi,
  FacilityOrderPackBagsApi,
  FacilityOrders,
  FacilityOrdersApi,
  FacilityOrdersHistory,
  FacilityPastOrdersApi,
  FacilityServiceFilter,
  FacilityServiceFilterApi,
} from '@/providers/laundryheap/interfaces/facility-orders';
import {
  FacilityWorker,
  FacilityWorkerQRScanApi,
  FacilityWorkerToken,
  SignInFacilityWorkerApiInterface,
  SignUpFacilityWorkerApiInterface,
} from '@/providers/laundryheap/interfaces/facility-worker';
import {
  ApolloQueryResult,
  ObservableQuery,
  SingleExecutionResult,
} from '@apollo/client/core';
import { GraphQLError } from 'graphql/error/GraphQLError';
import {
  AddAssociatedTagData,
  AddFacilityItemPhoto,
  AddFacilityProblemPhotoApiData,
  AddItemToFacilityOrderBag,
  AddPackageToFacilityOrder,
  AddSpecialItemToFacilityOrderBag,
  DeleteAssociatedTagData,
  DeleteAssociatedTagsData,
  DeleteFacilityProblemApiData,
  DeleteFacilityProblemPhotoApiData,
  EditFacilityItemPhoto,
  EditFacilityOrderBagSpecialItem,
  EditFacilityProblemApiData,
  EditFacilityProblemPhotoApiData,
  EditItemInFacilityOrderBag,
  EditPackageToFacilityOrder,
  FacilityOrderFinishPacking,
  FacilityProblemReportApiData,
  FinishBagPacking,
  FinishOrderBagItemisation,
  GetFacilityOrderBagData,
  GetFacilityOrderData,
  GetFacilityPastOrderData,
  GetFacilityServicesData,
  GetItemFromFacilityOrderBag,
  GetServiceMismatchListApiData,
  RemoveItemFromFacilityOrderBag,
  SignInFacilityWorkerData,
  SignUpFacilityWorkerData,
} from '@/providers/laundryheap/types';
import {
  FacilityService,
  FacilityServicesApi,
} from '@/providers/laundryheap/interfaces/facility-services';
import {
  ServiceMismatchItemApi,
  ServiceMismatchItems,
} from '@/providers/laundryheap/interfaces/services';
import {
  AddProblemPhotoApi,
  editProblemPhotoApi,
  EditReportedProblemApi,
  FacilityConfirmProblemApi,
  FacilityOrderBagConnectTagApi,
  FacilityOrderBagRemoveTagApi,
  FacilityOrderBagRemoveTagsApi,
  FacilityOrderProblem,
  FacilityProblem,
  FacilityProblemsListApi,
  ProblemDeletionApi,
  ProblemPhotoDeletionApi,
  ReportedProblem,
  ReportedProblemApi,
} from '@/providers/laundryheap/interfaces/problems';
import {
  OrdersWithProblem,
  OrdersWithProblemApi,
} from '@/providers/laundryheap/interfaces/support';
import { Country, CountryApiResponse, Facility } from '@/interfaces';
import store from '@/store';
import { StoreMutations } from '@/helpers/enums';
import { LaundryheapApiError } from '@/errors/api';
import { FacilityApi } from '@/providers/laundryheap/interfaces/facility';
import { captureException } from '@sentry/vue';

export interface ErrorResponse {
  short_code?: string;
  error?: string;
  status: boolean;
}

export interface Response<T> extends ErrorResponse {
  data?: T;
  message?: string;
}

export interface ResultError extends GraphQLError {
  short_code?: string;
}

export interface Result<TData> extends SingleExecutionResult<TData> {
  errors?: ReadonlyArray<ResultError>;
}

export interface WatchResult<TData> extends ApolloQueryResult<TData> {
  errors?: ReadonlyArray<ResultError>;
}

export async function getMyFacility(): Promise<Response<Facility>> {
  try {
    const response: Result<FacilityApi> = await client.query({
      query: getFacility,
    });

    return constructApiReturnData(response, 'facility');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacility',
      (e as Error).message
    );
  }
}

export const signUpFacilityWorker = async (
  data: SignUpFacilityWorkerData
): Promise<Response<FacilityWorker>> => {
  try {
    const response: Result<SignUpFacilityWorkerApiInterface> =
      await client.mutate({
        mutation: signUpFacilityWorkerSchema,
        variables: data,
      });

    return constructApiReturnData(response, 'signUpFacilityWorker');
  } catch (e) {
    return errorReporting(
      'Graphql error for signupFacilityWorker',
      (e as Error).message
    );
  }
};

export const signInFacilityWorker = async (
  data: SignInFacilityWorkerData
): Promise<Response<FacilityWorkerToken>> => {
  try {
    const response: Result<SignInFacilityWorkerApiInterface> =
      await client.mutate({
        mutation: signInFacilityWorkerSchema,
        variables: data,
      });

    return constructApiReturnData(response, 'signInFacilityWorker');
  } catch (e) {
    return errorReporting(
      'Graphql error for signInFacilityWorker',
      (e as Error).message
    );
  }
};

export const getFacilityOrders = async (): Promise<
  Response<FacilityOrders>
> => {
  try {
    const response: Result<FacilityOrdersApi> = await client.query({
      query: getFacilityOrdersSchema,
      fetchPolicy: 'network-only',
    });

    return constructApiReturnData(response, 'facilityOrders');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrders',
      (e as Error).message
    );
  }
};

/**
 * Get facility orders incrementally. This is useful for when you want to show
 * the orders as they are being fetched. Provided `callback` gets called with partially updated
 * facility orders as they are fetched. You can also provide `existingOrders` to keep old orders in the
 * state until the new replacement ones are fetched.
 * @param options
 */
export const getFacilityOrdersIncremental = async (options: {
  callback: (orders: FacilityOrders) => unknown;
  existingOrders?: FacilityOrders | null;
}): Promise<FacilityOrders | null> => {
  try {
    const facilityOrdersResponse: FacilityOrders = options.existingOrders
      ? {
          ...options.existingOrders,
        }
      : {
          today: [],
          tomorrow: [],
          future: [],
          past: [],
        };

    await Promise.all([
      getFacilityOrdersToday().then((response) => {
        if (response.data?.today) {
          facilityOrdersResponse.today = response.data.today;
          options.callback(facilityOrdersResponse);
        }
      }),
      getFacilityOrdersTomorrow().then((response) => {
        if (response.data?.tomorrow) {
          facilityOrdersResponse.tomorrow = response.data.tomorrow;
          options.callback(facilityOrdersResponse);
        }
      }),
      getFacilityOrdersFuturePast().then((response) => {
        if (response.data?.future && response.data?.past) {
          facilityOrdersResponse.future = response.data.future;
          facilityOrdersResponse.past = response.data.past;
          options.callback(facilityOrdersResponse);
        }
      }),
    ]);

    return facilityOrdersResponse;
  } catch (e) {
    captureException(e);
    return null;
  }
};

export const getFacilityOrdersCached = (): FacilityOrders | null => {
  try {
    const facilityOrders =
      (client.readQuery({
        query: getFacilityOrdersSchema,
      })?.facilityOrders as FacilityOrders) || null;

    return facilityOrders;
  } catch (e) {
    return null;
  }
};

export const getFacilityOrdersToday = async (): Promise<
  Response<FacilityOrders>
> => {
  try {
    const response: Result<FacilityOrdersApi> = await client.query({
      query: getFacilityOrdersTodaySchema,
      fetchPolicy: 'network-only',
    });

    return constructApiReturnData(response, 'facilityOrders');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrdersToday',
      (e as Error).message
    );
  }
};

export const getFacilityOrdersTomorrow = async (): Promise<
  Response<FacilityOrders>
> => {
  try {
    const response: Result<FacilityOrdersApi> = await client.query({
      query: getFacilityOrdersTomorrowSchema,
      fetchPolicy: 'network-only',
    });

    return constructApiReturnData(response, 'facilityOrders');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrdersTomorrow',
      (e as Error).message
    );
  }
};

export const getFacilityOrdersFuturePast = async (): Promise<
  Response<FacilityOrders>
> => {
  try {
    const response: Result<FacilityOrdersApi> = await client.query({
      query: getFacilityOrdersFuturePastSchema,
      fetchPolicy: 'network-only',
    });

    return constructApiReturnData(response, 'facilityOrders');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrdersFuturePast',
      (e as Error).message
    );
  }
};

export async function watchFacilityOrders(
  callback: (result: Response<FacilityOrders>) => void
): Promise<Response<FacilityOrders>> {
  try {
    const query: ObservableQuery<FacilityOrdersApi> = client.watchQuery({
      query: getFacilityOrdersSchema,
      fetchPolicy: 'cache-and-network',
    });

    const result = await query.result();

    if (!result.errors && result.loading) {
      const subscription = query.subscribe({
        next(newResult: WatchResult<FacilityOrdersApi>): void {
          if (!newResult.errors && !newResult.loading) {
            const constructedResponse = constructApiReturnData(
              newResult,
              'facilityOrders'
            );
            callback(constructedResponse);
            subscription.unsubscribe();
          }
        },
      });
    }

    return constructApiReturnData(result, 'facilityOrders');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrders',
      (e as Error).message
    );
  }
}

/**
 * Get facility services used for filters
 */
export const getFacilityServiceFilters = async (): Promise<
  Response<FacilityServiceFilter[]>
> => {
  try {
    const response: Result<FacilityServiceFilterApi> = await client.query({
      fetchPolicy: 'cache-first',
      query: getFacilityServiceFilterSchema,
    });

    return constructApiReturnData(response, 'facilityServiceFilter');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityServiceFilters',
      (e as Error).message
    );
  }
};

export const getFacilityOrdersHistory = async (
  data: GetFacilityPastOrderData
): Promise<Response<FacilityOrdersHistory>> => {
  try {
    const response: Result<FacilityPastOrdersApi> = await client.query({
      query: getFacilityPastOrdersSchema,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityPastOrders');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrdersHistory',
      (e as Error).message
    );
  }
};

export const getFacilityData = async (
  qrCode: string
): Promise<Response<FacilityWorker>> => {
  try {
    const response: Result<FacilityWorkerQRScanApi> = await client.query({
      query: facilityWorkerQRScan,
      variables: { qrCode },
    });

    return constructApiReturnData(response, 'facilityWorkerQRScan');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityData',
      (e as Error).message
    );
  }
};

export const getFacilityOrder = async (
  data: GetFacilityOrderData
): Promise<Response<FacilityOrder>> => {
  try {
    const response: Result<FacilityOrderApi> = await client.query({
      query: facilityOrder,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrder');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrder',
      (e as Error).message
    );
  }
};

/**
 * Fetch facility order bag
 * @param data
 */
export const getFacilityOrderBag = async (
  data: GetFacilityOrderBagData
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagApi> = await client.query({
      query: facilityOrderBag,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderBag');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Same as getFacilityOrderBag just this one is using cache and watcher for
 * getting api data
 * @param data
 */
export const watchFacilityOrderBag = async (
  data: GetFacilityOrderBagData
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: ObservableQuery<
      FacilityOrderBagApi,
      GetFacilityOrderBagData
    > = await client.watchQuery({
      query: facilityOrderBag,
      variables: data,
    });

    const result: WatchResult<FacilityOrderBagApi> = await response.result();

    if (!result.errors && result.loading) {
      const subscription = response.subscribe({
        next(subResult: WatchResult<FacilityOrderBagApi>): void {
          const dataTest = constructApiReturnData(
            subResult,
            'facilityOrderBag'
          );
          if (!subResult.errors && !subResult.loading && dataTest.status) {
            store.commit(StoreMutations.addFacilityOrderBag, dataTest.data);
          }

          // Unsubscribe after new data is loaded
          if (!subResult.loading) {
            subscription.unsubscribe();
          }
        },
      });
    }
    return constructApiReturnData(result, 'facilityOrderBag');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Get all the services for selected bag
 * @param data
 */
export const getFacilityServices = async (
  data: GetFacilityServicesData
): Promise<Response<FacilityService[]>> => {
  try {
    const response: ObservableQuery<
      FacilityServicesApi,
      GetFacilityServicesData
    > = await client.watchQuery({
      query: facilityServices,
      variables: data,
    });

    const result: WatchResult<FacilityServicesApi> = await response.result();

    if (!result.errors && result.loading) {
      const subscription = response.subscribe({
        next(subResult: WatchResult<FacilityServicesApi>): void {
          const data = constructApiReturnData(subResult, 'facilityServices');
          if (!subResult.errors && !subResult.loading && data.status) {
            store.commit(StoreMutations.addFacilityBagServices, data.data);
          }

          // Unsubscribe after new data is loaded
          if (!subResult.loading) {
            subscription.unsubscribe();
          }
        },
      });
    }

    return constructApiReturnData(result, 'facilityServices');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Add the item to the bag
 * @param data
 */
export const addItemToFacilityOrderBag = async (
  data: AddItemToFacilityOrderBag
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagAddItemApi> = await client.mutate({
      mutation: facilityOrderBagAddItem,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderBagAddItem');
  } catch (e) {
    return errorReporting(
      'Graphql error for addItemToFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Edit item inside bag
 * @param data
 */
export const editItemInFacilityOrderBag = async (
  data: EditItemInFacilityOrderBag
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagEditItemApi> = await client.mutate({
      mutation: facilityOrderBagEditItem,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderBagEditItem');
  } catch (e) {
    return errorReporting(
      'Graphql error for editItemInFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Remove item from the bag
 * @param data - RemoveItemFromFacilityOrderBag
 * @return Promise<Response<FacilityOrderBag>>
 */
export const removeItemFromFacilityOrderBag = async (
  data: RemoveItemFromFacilityOrderBag
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagRemoveItemApi> = await client.mutate(
      {
        mutation: facilityOrderBagRemoveItem,
        variables: data,
      }
    );

    return constructApiReturnData(response, 'facilityOrderBagRemoveItem');
  } catch (e) {
    return errorReporting(
      'Graphql error for removeItemFromFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - create and add special item to the facility bag
 * IT IS USED ALSO FOR REPROCESSING ITEMS AND NOT JUST SPECIAL ITEMS
 * @param data - AddSpecialItemToFacilityOrderBag
 * @return Promise<Response<FacilityOrderBag>>
 */
export const addSpecialItemToFacilityOrderBag = async (
  data: AddSpecialItemToFacilityOrderBag
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagAddSpecialItemApi> =
      await client.mutate({
        mutation: facilityOrderBagAddSpecialItem,
        variables: data,
      });

    return constructApiReturnData(response, 'facilityOrderBagAddSpecialItem');
  } catch (e) {
    return errorReporting(
      'Graphql error for addSpecialItemToFacilityOrderBag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Edit facility bag special item
 * @param data - EditFacilityOrderBagSpecialItem
 * @param handledErrors - list of error codes that are already handled in the UI and should not trigger a global popup
 * @return Promise<Response<FacilityOrderBag>>
 */
export const editFacilityOrderBagSpecialItem = async (
  data: EditFacilityOrderBagSpecialItem,
  handledErrors: string[] = []
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagEditSpecialItemApi> =
      await client.mutate({
        mutation: facilityOrderBagEditSpecialItem,
        variables: data,
      });

    return constructApiReturnData(
      response,
      'facilityOrderBagEditSpecialItem',
      handledErrors
    );
  } catch (e) {
    return errorReporting(
      'Graphql error for editFacilityOrderBagSpecialItem',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - fetch data for facility order item item
 * @param data - GetItemFromFacilityOrderBag
 * @return Promise<Response<FacilityOrderBagItem>>
 */
export const getFacilityOrderBagItem = async (
  data: GetItemFromFacilityOrderBag
): Promise<Response<FacilityOrderBagItem>> => {
  try {
    const response: Result<FacilityOrderItemApi> = await client.query({
      query: getFacilityOrderItem,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderItem');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityOrderBagItem',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - edit image of the special item
 * @param data - EditFacilityItemPhoto
 * @return Promise<Response<boolean>>
 */
export const editFacilityOrderItemPhoto = async (
  data: EditFacilityItemPhoto
): Promise<Response<FacilityOrderBagItemPhotos>> => {
  try {
    const response: Result<EditFacilityOrderItemPhotoApi> = await client.mutate(
      {
        mutation: facilityOrderEditItemPhoto,
        variables: data,
      }
    );

    return constructApiReturnData(response, 'facilityOrderEditItemPhoto');
  } catch (e) {
    return errorReporting(
      'Graphql error for editFacilityOrderItemPhoto',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Add image to the special item
 * @param data - AddFacilityItemPhoto
 * @return Promise<Response<boolean>>
 */
export const addFacilityOrderItemPhoto = async (
  data: AddFacilityItemPhoto
): Promise<Response<FacilityOrderBagItemPhotos>> => {
  try {
    const response: Result<AddFacilityOrderItemPhotoApi> = await client.mutate({
      mutation: facilityOrderAddItemPhoto,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderAddItemPhoto');
  } catch (e) {
    return errorReporting(
      'Graphql error for addFacilityOrderItemPhoto',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - finish the itemisation of the order bag
 * @param data - FinishOrderBagItemisation
 * @return Promise<Response<FacilityOrderBag>>
 */
export const finishOrderBagItemisation = async (
  data: FinishOrderBagItemisation
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagFinishItemisationApi> =
      await client.mutate({
        mutation: facilityOrderBagFinishItemisation,
        variables: data,
      });

    return constructApiReturnData(
      response,
      'facilityOrderBagFinishItemisation'
    );
  } catch (e) {
    return errorReporting(
      'Graphql error for finishOrderBagItemisation',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - add associated bag tag
 * @param data - AddAssociatedTagData
 * @return Promise<Response<FacilityOrderBag>>
 */
export const addAssociatedTag = async (
  data: AddAssociatedTagData
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagConnectTagApi> = await client.mutate(
      {
        mutation: facilityOrderBagConnectTag,
        variables: data,
      }
    );

    return constructApiReturnData(response, 'facilityOrderBagConnectTag');
  } catch (e) {
    return errorReporting(
      'Graphql error for addAssociatedTag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Delete associated bag tag
 * @param data - DeleteAssociatedTagData
 * @return Promise<Response<FacilityOrderBag>>
 */
export const deleteAssociatedTag = async (
  data: DeleteAssociatedTagData
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagRemoveTagApi> = await client.mutate({
      mutation: facilityOrderBagRemoveTag,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderBagRemoveTag');
  } catch (e) {
    return errorReporting(
      'Graphql error for deleteAssociatedTag',
      (e as Error).message
    );
  }
};

/**
 * Itemisation - Delete multiple associated bag tags
 * @param data - DeleteAssociatedTagsData
 * @return Promise<Response<FacilityOrderBag>>
 */
export const deleteAssociatedTags = async (
  data: DeleteAssociatedTagsData
): Promise<Response<FacilityOrderBag>> => {
  try {
    const response: Result<FacilityOrderBagRemoveTagsApi> = await client.mutate(
      {
        mutation: facilityOrderBagRemoveTags,
        variables: data,
      }
    );

    return constructApiReturnData(response, 'facilityOrderBagRemoveTags');
  } catch (e) {
    return errorReporting(
      'Graphql error for deleteAssociatedTags',
      (e as Error).message
    );
  }
};

/**
 * Packing - Add a bag/hangers to the facility order
 * @param data - AddPackageToFacilityOrder
 * @return Promise<Response<FacilityOrder>>
 */
export const addPackageToFacilityOrder = async (
  data: AddPackageToFacilityOrder
): Promise<Response<FacilityOrder>> => {
  try {
    const response: Result<FacilityOrderAddPackagesApi> = await client.mutate({
      mutation: facilityOrderAddPackages,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderAddPackages');
  } catch (e) {
    return errorReporting(
      'Graphql error for addPackageToFacilityOrder',
      (e as Error).message
    );
  }
};

/**
 * Packing - Finish bag packing without any tags
 * @param data - FinishBagPacking
 * @return Promise<Response<FacilityOrder>>
 */
export const finishBagPacking = async (
  data: FinishBagPacking
): Promise<Response<FacilityOrder>> => {
  try {
    const response: Result<FacilityOrderPackBagsApi> = await client.mutate({
      mutation: facilityOrderPackBags,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderPackBags');
  } catch (e) {
    return errorReporting(
      'Graphql error for finishBagPacking',
      (e as Error).message
    );
  }
};

/**
 * Packing - Edit a bag/hangers to the facility order. If empty it will delete the package.
 * @param data - EditPackageToFacilityOrder
 * @return Promise<Response<FacilityOrder>>
 */
export const editPackageToFacilityOrder = async (
  data: EditPackageToFacilityOrder
): Promise<Response<FacilityOrder>> => {
  try {
    const response: Result<FacilityOrderEditPackagesApi> = await client.mutate({
      mutation: facilityOrderEditPackages,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderEditPackages');
  } catch (e) {
    return errorReporting(
      'Graphql error for editPackageToFacilityOrder',
      (e as Error).message
    );
  }
};

/**
 * Packing - Finish packing of the facility order
 * @param data - FacilityOrderFinishPacking
 * @return Promise<Response<FacilityOrder>>
 */
export const finishPackingOfFacilityOrder = async (
  data: FacilityOrderFinishPacking
): Promise<Response<FacilityOrder>> => {
  try {
    const response: Result<FacilityOrderFinishPackingApi> = await client.mutate(
      {
        mutation: facilityOrderFinishPacking,
        variables: data,
      }
    );

    return constructApiReturnData(response, 'facilityOrderFinishPacking');
  } catch (e) {
    return errorReporting(
      'Graphql error for finishPackingOfFacilityOrder',
      (e as Error).message
    );
  }
};

/*************************************
 * PROBLEM REPORTING APIS
 *************************************/

/**
 * Problem - Get the list of service mismatch services
 * @param data - GetServiceMismatchListApiData
 * @return Promise<Response<ServiceMismatchItems>>
 */
export const getFacilityServiceMismatchList = async (
  data: GetServiceMismatchListApiData
): Promise<Response<ServiceMismatchItems>> => {
  try {
    // We must not cache this list as when you do change a service the list
    // selection needs to change
    const response: Result<ServiceMismatchItemApi> = await client.query({
      query: facilityServiceMismatchList,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityServiceMismatchList');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityServiceMismatchList',
      (e as Error).message
    );
  }
};

/**
 * Problem - Get the list of problem reports
 * @return Promise<Response<FacilityProblem[]>>
 */
export const getFacilityProblemsList = async (): Promise<
  Response<FacilityProblem[]>
> => {
  try {
    const response: Result<FacilityProblemsListApi> = await client.query({
      fetchPolicy: 'cache-first',
      query: facilityProblemsList,
    });

    return constructApiReturnData(response, 'facilityProblemsList');
  } catch (e) {
    return errorReporting(
      'Graphql error for getFacilityProblemsList',
      (e as Error).message
    );
  }
};

/**
 * Problem - Send problem report for the facility
 * @param data - FacilityProblemReportApiData
 * @return Promise<Response<ReportedProblem>>
 */
export const reportAFacilityProblem = async (
  data: FacilityProblemReportApiData
): Promise<Response<ReportedProblem>> => {
  try {
    const response: Result<ReportedProblemApi> = await client.mutate({
      mutation: facilityProblemReport,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityProblemReport');
  } catch (e) {
    return errorReporting(
      'Graphql error for reportAFacilityProblem',
      (e as Error).message
    );
  }
};

/**
 * Problem - Edit reported problem for the facility
 * @param data - EditFacilityProblemApiData
 * @return Promise<Response<ReportedProblem>>
 */
export const editReportedFacilityProblem = async (
  data: EditFacilityProblemApiData
): Promise<Response<ReportedProblem>> => {
  try {
    const response: Result<EditReportedProblemApi> = await client.mutate({
      mutation: facilityEditProblem,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityEditProblem');
  } catch (e) {
    return errorReporting(
      'Graphql error for editReportedFacilityProblem',
      (e as Error).message
    );
  }
};

/**
 * Problem - Delete facility problem
 * @param data - DeleteFacilityProblemApiData
 * @return Promise<Response<boolean>>
 */
export const deleteAFacilityProblem = async (
  data: DeleteFacilityProblemApiData
): Promise<Response<boolean>> => {
  try {
    const response: Result<ProblemDeletionApi> = await client.mutate({
      mutation: facilityDeleteProblem,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityDeleteProblem');
  } catch (e) {
    return errorReporting(
      'Graphql error for deleteAFacilityProblem',
      (e as Error).message
    );
  }
};

/**
 * Problem - Add problem report photo for the facility
 * @param data - AddFacilityProblemPhotoApiData
 * @return Promise<Response<FacilityOrderBagItemProblemPhotos>>
 */
export const addFacilityProblemPhoto = async (
  data: AddFacilityProblemPhotoApiData
): Promise<Response<FacilityOrderBagItemProblemPhotos>> => {
  try {
    const response: Result<AddProblemPhotoApi> = await client.mutate({
      mutation: facilityOrderAddProblemPhoto,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderAddProblemPhoto');
  } catch (e) {
    return errorReporting(
      'Graphql error for addFacilityProblemPhoto',
      (e as Error).message
    );
  }
};

/**
 * Problem - Edit problem report photo for the facility
 * @param data - EditFacilityProblemPhotoApiData
 * @return Promise<Response<FacilityOrderBagItemProblemPhotos>>
 */
export const editFacilityProblemPhoto = async (
  data: EditFacilityProblemPhotoApiData
): Promise<Response<FacilityOrderBagItemProblemPhotos>> => {
  try {
    const response: Result<editProblemPhotoApi> = await client.mutate({
      mutation: facilityOrderEditProblemPhoto,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderEditProblemPhoto');
  } catch (e) {
    return errorReporting(
      'Graphql error for editFacilityProblemPhoto',
      (e as Error).message
    );
  }
};

/**
 * Problem - Delete facility problem photo
 * @param data - DeleteFacilityProblemPhotoApiData
 * @return Promise<Response<boolean>>
 */
export const deleteFacilityProblemPhoto = async (
  data: DeleteFacilityProblemPhotoApiData
): Promise<Response<boolean>> => {
  try {
    const response: Result<ProblemPhotoDeletionApi> = await client.mutate({
      mutation: facilityOrderRemoveProblemPhoto,
      variables: data,
    });

    return constructApiReturnData(response, 'facilityOrderRemoveProblemPhoto');
  } catch (e) {
    return errorReporting(
      'Graphql error for deleteAFacilityProblem',
      (e as Error).message
    );
  }
};

/**
 * Problem - Get the list of service mismatch services
 * @return Promise<Response<OrdersWithProblem>>
 */
export const getSupportCenterOrderList = async (): Promise<
  Response<OrdersWithProblem>
> => {
  try {
    const response: Result<OrdersWithProblemApi> = await client.query({
      fetchPolicy: 'network-only',
      query: supportCenterOrderList,
    });

    return constructApiReturnData(response, 'ordersWithProblems');
  } catch (e) {
    return errorReporting(
      'Graphql error for getSupportCenterOrderList',
      (e as Error).message
    );
  }
};

/**
 * Problem - Get the ids of only received problems
 * @return Promise<Response<OrdersWithProblem>>
 */
export const watchReceivedSupportCenterOrderList = async (): Promise<
  Response<OrdersWithProblem>
> => {
  try {
    const response: ObservableQuery<OrdersWithProblemApi, OrdersWithProblem> =
      await client.watchQuery({
        pollInterval: 5000,
        query: receivedOrdersWithProblems,
      });

    const result: WatchResult<OrdersWithProblemApi> = await response.result();

    if (!result.errors) {
      response.subscribe({
        next(subResult: WatchResult<OrdersWithProblemApi>): void {
          const dataTest = constructApiReturnData(
            subResult,
            'ordersWithProblems'
          );
          if (!subResult.errors && !subResult.loading && dataTest.status) {
            store.commit(
              StoreMutations.setSupportNotification,
              dataTest.data?.received.length || null
            );
          }
        },
      });
    }
    return constructApiReturnData(result, 'ordersWithProblems');
  } catch (e) {
    return errorReporting(
      'Graphql error for watchReceivedSupportCenterOrderList',
      (e as Error).message
    );
  }
};

/**
 * Problem - Confirm a facility order problem
 * @return Promise<Response<OrdersWithProblem>>
 */
export const confirmFacilityOrderProblem = async (
  facilityOrderProblemId: string
): Promise<Response<FacilityOrderProblem>> => {
  try {
    const response: Result<FacilityConfirmProblemApi> = await client.mutate({
      mutation: facilityConfirmProblem,
      variables: { facilityOrderProblemId },
    });

    return constructApiReturnData(response, 'facilityConfirmProblem');
  } catch (e) {
    return errorReporting(
      'Graphql error for confirmFacilityOrderProblem',
      (e as Error).message
    );
  }
};

export const getCountries = async (): Promise<Response<Country[]>> => {
  try {
    const response: Result<CountryApiResponse> = await client.query({
      fetchPolicy: 'cache-first',
      query: countries,
    });

    return constructApiReturnData(response, 'countries');
  } catch (e) {
    return errorReporting(
      'Graphql error for getCountries',
      (e as Error).message
    );
  }
};

export const getSurveyAnswered = async (id: string): Promise<boolean> => {
  return client
    .query({
      fetchPolicy: 'network-only',
      query: surveyAnswered,
      variables: {
        id,
      },
    })
    .then((res) => res.data.surveyAnswered as boolean)
    .catch(() => false);
};

/*************************************
 * PRIVATE HELPERS
 *************************************/

/**
 * Construct the data for returning api. Also handle the errors
 * @param response
 * @param key
 * @param handledErrors - if error is handled, we do not show global error popup
 */
function constructApiReturnData<TResult>(
  response: Result<TResult> | WatchResult<TResult>,
  key: keyof TResult,
  handledErrors: string[] = []
): Response<TResult[keyof TResult]> {
  if (response.errors) {
    const message = response.errors[0].message;

    const skipSentryReportsForMessages = ['Not found'];
    const shortCode = response.errors[0].short_code;
    const sentryKey = skipSentryReportsForMessages.includes(message)
      ? 'error'
      : shortCode;

    if (!shortCode || !handledErrors.includes(shortCode)) {
      // If we have short code then we show the message to customer and it is only info
      new LaundryheapApiError(
        shortCode ? 'info' : 'error',
        sentryKey ? `API Error: ${key}: ${shortCode}` : undefined,
        message
      );
    }
    return { status: false, error: message, short_code: shortCode };
  }

  if (response.data && key in response.data && response.data[key]) {
    return { status: true, data: response.data[key] };
  }

  return errorReporting(
    'Graphql error, missing data and error key',
    JSON.stringify(response)
  );
}

const errorReporting = (key?: string, message?: string): ErrorResponse => {
  new LaundryheapApiError('error', key, message);
  return { status: false, error: message };
};
